import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { SESSION_STORAGE_KEYS } from '../session_storage/constants/session-storage-keys.constant';
import {
  persistStoredAccountSelection,
  readStoredAccountHomeId,
  resolveAccountForTenant,
} from './account-selection.util';
import { setMsalTenantId } from './msal.factory';

/** Prepare the global InstanceAdmin entry only; never use this for ordinary resource requests. */
export function prepareInstanceAdminEntry(
  instance: Pick<IPublicClientApplication, 'getAllAccounts' | 'getActiveAccount' | 'setActiveAccount'>,
): AccountInfo | null {
  const accounts = instance.getAllAccounts();
  const active = instance.getActiveAccount();
  const tenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
  let account = resolveAccountForTenant(accounts, active, tenantId);

  if (!account) {
    // Only recover an unavailable tenant, not a missing selected person in an existing tenant.
    if (accounts.some((candidate) => candidate.tenantId === tenantId)) return null;
    account = active
      ? (accounts.find(
          (candidate) => candidate.tenantId === active.tenantId && candidate.homeAccountId === active.homeAccountId,
        ) ?? null)
      : accounts.length === 1
        ? accounts[0]
        : null;
    const savedPerson = readStoredAccountHomeId(tenantId);
    if (savedPerson && account?.homeAccountId !== savedPerson) return null;
  }

  if (!account?.tenantId || !account.homeAccountId) return null;
  const roles = account.idTokenClaims?.roles;
  if (
    !Array.isArray(roles) ||
    !roles.some((role) => typeof role === 'string' && role.toLowerCase() === 'instanceadmin')
  ) {
    return null;
  }
  const savedPerson = readStoredAccountHomeId(account.tenantId);
  if (savedPerson && savedPerson !== account.homeAccountId) return null;

  // Bind a normal, explicit context so the strict interceptor and role readers agree.
  // Cached roles control navigation only; MSAL and the backend still validate each protected request.
  localStorage.setItem(SESSION_STORAGE_KEYS.TENANT_ID, account.tenantId);
  setMsalTenantId(account.tenantId);
  instance.setActiveAccount(account);
  persistStoredAccountSelection(account);
  return account;
}
